import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Quote } from '../types/admin';
import { Job } from './useJobs';

// ── Types ────────────────────────────────────────────────────────────────────

export type PriorityTier = 1 | 2 | 3 | 4;

export interface DashboardItem {
  id: string;
  type: 'lead' | 'quote' | 'job';
  title: string;
  subtitle?: string;
  reason: string;
  tier: PriorityTier;
  /** ISO date used for timeline sorting */
  date?: string;
  /** ISO date used for deadline proximity */
  deadline?: string;
  /** Dollar value for tie-breaking */
  value?: number;
  /** Days since last status change or creation */
  staleDays: number;
  /** Link to navigate to */
  link: string;
  /** Status badge */
  status: string;
  statusClasses: string;
  /** Raw data for reference */
  raw: Quote | Job;
}

export interface TimelineGroup {
  label: string;
  items: DashboardItem[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function daysBetween(a: Date, b: Date): number {
  const msPerDay = 86400000;
  return Math.floor((b.getTime() - a.getTime()) / msPerDay);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function parseDate(iso?: string): Date | null {
  if (!iso) return null;
  return iso.includes('T') ? new Date(iso) : new Date(`${iso}T00:00`);
}

// ── Status class maps (mirroring types/admin.ts) ────────────────────────────

const LEAD_STATUS_CLASSES: Record<string, string> = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-amber-50 text-amber-700',
  estimate_scheduled: 'bg-emerald-50 text-emerald-700',
  not_pursuing: 'bg-stone text-slate',
  lost: 'bg-rose/10 text-rose',
};

const LEAD_STATUS_LABEL: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  estimate_scheduled: 'Estimate Scheduled',
  not_pursuing: 'Not Pursuing',
  lost: 'Lost',
};

const QUOTE_STATUS_CLASSES: Record<string, string> = {
  draft: 'bg-stone text-slate',
  sent: 'bg-plum/10 text-plum',
  response_received: 'bg-sky-50 text-sky-700',
  booked: 'bg-emerald-50 text-emerald-700',
  declined: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-rose/10 text-rose',
};

const QUOTE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  response_received: 'Response',
  booked: 'Booked',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

const JOB_STATUS_CLASSES: Record<string, string> = {
  scheduled: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  on_hold: 'bg-stone text-slate',
  cancelled: 'bg-rose/10 text-rose',
};

const JOB_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

// ── Priority assignment ─────────────────────────────────────────────────────

function classifyLead(q: Quote, now: Date): DashboardItem | null {
  const leadStatus = q.leadStatus || 'new';
  // Skip dead leads
  if (leadStatus === 'not_pursuing' || leadStatus === 'lost') return null;

  const createdAt = parseDate(q.createdAt);
  const updatedAt = parseDate(q.updatedAt);
  const staleDays = createdAt ? daysBetween(createdAt, now) : 0;
  const daysSinceUpdate = updatedAt ? daysBetween(updatedAt, now) : staleDays;
  const followUpDate = parseDate(q.nextFollowUp);

  const base: Omit<DashboardItem, 'tier' | 'reason'> = {
    id: q.id,
    type: 'lead',
    title: q.customerCompanyName || q.pointOfContact?.name || 'Untitled Lead',
    subtitle: q.pointOfContact?.name,
    date: q.nextFollowUp || q.createdAt,
    deadline: q.nextFollowUp,
    value: q.estimatedValue,
    staleDays,
    link: `/moves/${q.id}`,
    status: LEAD_STATUS_LABEL[leadStatus] || leadStatus,
    statusClasses: LEAD_STATUS_CLASSES[leadStatus] || 'bg-stone text-slate',
    raw: q,
  };

  // Follow-up overdue
  if (followUpDate && followUpDate < now) {
    const overdueDays = daysBetween(followUpDate, now);
    if (overdueDays >= 5) {
      return { ...base, tier: 1, reason: `Follow-up ${overdueDays}d overdue` };
    }
    return { ...base, tier: 2, reason: `Follow-up ${overdueDays}d overdue` };
  }

  // Follow-up due today/tomorrow
  if (followUpDate) {
    const daysUntil = daysBetween(now, followUpDate);
    if (daysUntil <= 1) {
      return { ...base, tier: 2, reason: daysUntil === 0 ? 'Follow-up due today' : 'Follow-up due tomorrow' };
    }
  }

  // New lead uncontacted
  if (leadStatus === 'new') {
    if (staleDays >= 5) {
      return { ...base, tier: 1, reason: `New lead uncontacted for ${staleDays}d` };
    }
    if (staleDays >= 2) {
      return { ...base, tier: 2, reason: `New lead uncontacted for ${staleDays}d` };
    }
    return { ...base, tier: 3, reason: 'New lead — needs first contact' };
  }

  // Contacted but no estimate scheduled
  if (leadStatus === 'contacted') {
    if (daysSinceUpdate >= 7) {
      return { ...base, tier: 2, reason: `Contacted ${daysSinceUpdate}d ago, no estimate scheduled` };
    }
    return { ...base, tier: 3, reason: 'Contacted — schedule estimate' };
  }

  // Estimate scheduled — check if visit is soon
  if (leadStatus === 'estimate_scheduled') {
    const visitDate = parseDate(q.estimateVisitDate);
    if (visitDate) {
      const daysUntilVisit = daysBetween(startOfDay(now), startOfDay(visitDate));
      if (daysUntilVisit < 0) {
        return { ...base, tier: 2, reason: 'Estimate visit is past due' };
      }
      if (daysUntilVisit <= 1) {
        return { ...base, tier: 2, reason: daysUntilVisit === 0 ? 'Estimate visit today' : 'Estimate visit tomorrow' };
      }
    }
    return { ...base, tier: 4, reason: 'Estimate visit scheduled' };
  }

  return { ...base, tier: 4, reason: 'Lead in progress' };
}

function classifyQuote(q: Quote, now: Date): DashboardItem | null {
  const status = q.quoteStatus;
  // Skip terminal statuses
  if (status === 'cancelled' || status === 'declined') return null;

  const createdAt = parseDate(q.createdAt);
  const updatedAt = parseDate(q.updatedAt);
  const staleDays = updatedAt ? daysBetween(updatedAt, now) : (createdAt ? daysBetween(createdAt, now) : 0);
  const moveDate = q.movingDate?.preferredDate;
  const moveDateParsed = parseDate(moveDate);

  const base: Omit<DashboardItem, 'tier' | 'reason'> = {
    id: q.id,
    type: 'quote',
    title: q.customerCompanyName || 'Untitled Quote',
    subtitle: q.quoteNumber || undefined,
    date: moveDate || q.createdAt,
    deadline: moveDate,
    value: q.formula?.finalCostEstimate ?? q.estimatedValue,
    staleDays,
    link: `/moves/${q.id}`,
    status: QUOTE_STATUS_LABEL[status] || status,
    statusClasses: QUOTE_STATUS_CLASSES[status] || 'bg-stone text-slate',
    raw: q,
  };

  // Booked but no job status (not scheduled yet)
  if (status === 'booked' && !q.jobStatus) {
    if (moveDateParsed) {
      const daysUntilMove = daysBetween(startOfDay(now), startOfDay(moveDateParsed));
      if (daysUntilMove <= 14) {
        return { ...base, tier: 1, reason: `Booked — move in ${daysUntilMove}d, not scheduled` };
      }
    }
    return { ...base, tier: 2, reason: 'Booked — needs scheduling' };
  }

  // Sent but no response
  if (status === 'sent') {
    if (staleDays >= 7) {
      return { ...base, tier: 1, reason: `Sent ${staleDays}d ago — at risk` };
    }
    if (staleDays >= 3) {
      return { ...base, tier: 2, reason: `Sent ${staleDays}d ago — follow up` };
    }
    return { ...base, tier: 4, reason: 'Quote sent — awaiting response' };
  }

  // Response received — needs attention
  if (status === 'response_received') {
    if (staleDays >= 3) {
      return { ...base, tier: 1, reason: `Response ${staleDays}d ago — act now` };
    }
    return { ...base, tier: 2, reason: 'Client responded — review' };
  }

  // Draft
  if (status === 'draft') {
    const visitDate = parseDate(q.estimateVisitDate);
    if (visitDate) {
      const daysSinceVisit = daysBetween(startOfDay(visitDate), startOfDay(now));
      if (daysSinceVisit > 3) {
        return { ...base, tier: 2, reason: `Draft — ${daysSinceVisit}d since estimate visit` };
      }
    }
    if (staleDays >= 7) {
      return { ...base, tier: 3, reason: `Draft for ${staleDays}d — needs work` };
    }
    return { ...base, tier: 4, reason: 'Draft in progress' };
  }

  return { ...base, tier: 4, reason: 'Quote on track' };
}

function classifyJob(j: Job, now: Date): DashboardItem | null {
  const status = j.jobStatus;
  if (status === 'completed' || status === 'cancelled') return null;

  const moveDate = j.movingDate?.preferredDate;
  const moveDateParsed = parseDate(moveDate);
  const updatedAt = parseDate(j.updatedAt);
  const staleDays = updatedAt ? daysBetween(updatedAt, now) : 0;
  const hasCrew = (j.assignedCrew?.length ?? 0) > 0;
  const hasTruck = (j.assignedTrucks?.length ?? 0) > 0;
  const isFullyAssigned = hasCrew && hasTruck;

  const base: Omit<DashboardItem, 'tier' | 'reason'> = {
    id: j.id,
    type: 'job',
    title: j.customerCompanyName || 'Untitled Job',
    subtitle: j.quoteNumber || undefined,
    date: moveDate || j.createdAt,
    deadline: moveDate,
    value: j.logisticsSummary?.finalCostEstimate,
    staleDays,
    link: `/moves/${j.id}`,
    status: JOB_STATUS_LABEL[status] || status,
    statusClasses: JOB_STATUS_CLASSES[status] || 'bg-stone text-slate',
    raw: j,
  };

  if (!moveDateParsed) {
    return { ...base, tier: 2, reason: 'No move date set' };
  }

  const daysUntilMove = daysBetween(startOfDay(now), startOfDay(moveDateParsed));

  // In progress — always tier 1
  if (status === 'in_progress') {
    return { ...base, tier: 1, reason: 'Job in progress' };
  }

  // On hold
  if (status === 'on_hold') {
    if (daysUntilMove <= 7) {
      return { ...base, tier: 1, reason: `On hold — move in ${daysUntilMove}d` };
    }
    return { ...base, tier: 2, reason: 'Job on hold' };
  }

  // Scheduled
  if (status === 'scheduled') {
    // Today/tomorrow + not fully assigned
    if (daysUntilMove <= 1 && !isFullyAssigned) {
      return { ...base, tier: 1, reason: `${daysUntilMove === 0 ? 'Today' : 'Tomorrow'} — missing ${!hasCrew ? 'crew' : ''}${!hasCrew && !hasTruck ? ' & ' : ''}${!hasTruck ? 'truck' : ''}` };
    }
    // Today/tomorrow, fully assigned
    if (daysUntilMove <= 1) {
      return { ...base, tier: 1, reason: daysUntilMove === 0 ? 'Move is today' : 'Move is tomorrow' };
    }
    // This week, not fully assigned
    if (daysUntilMove <= 7 && !isFullyAssigned) {
      return { ...base, tier: 2, reason: `Move in ${daysUntilMove}d — not fully assigned` };
    }
    // This week, assigned
    if (daysUntilMove <= 7) {
      return { ...base, tier: 3, reason: `Move in ${daysUntilMove}d — ready` };
    }
    // Overdue
    if (daysUntilMove < 0) {
      return { ...base, tier: 1, reason: `Move date was ${Math.abs(daysUntilMove)}d ago` };
    }
    // Further out
    return { ...base, tier: 4, reason: `Move in ${daysUntilMove}d` };
  }

  return { ...base, tier: 4, reason: 'Job on track' };
}

// ── Sort within tier ────────────────────────────────────────────────────────

function sortItems(items: DashboardItem[]): DashboardItem[] {
  return [...items].sort((a, b) => {
    // 1. Tier
    if (a.tier !== b.tier) return a.tier - b.tier;
    // 2. Deadline proximity
    const aDeadline = a.deadline ? new Date(a.deadline).getTime() : Infinity;
    const bDeadline = b.deadline ? new Date(b.deadline).getTime() : Infinity;
    if (aDeadline !== bDeadline) return aDeadline - bDeadline;
    // 3. Staleness (more stale = higher)
    if (a.staleDays !== b.staleDays) return b.staleDays - a.staleDays;
    // 4. Value (higher = higher priority)
    const aVal = a.value ?? 0;
    const bVal = b.value ?? 0;
    if (aVal !== bVal) return bVal - aVal;
    return 0;
  });
}

// ── Timeline grouping ───────────────────────────────────────────────────────

export function groupByTimeline(items: DashboardItem[], now: Date): TimelineGroup[] {
  const today = startOfDay(now);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - today.getDay()));
  const endOfNextWeek = new Date(endOfWeek);
  endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);

  const groups: Record<string, DashboardItem[]> = {
    'Overdue': [],
    'Today': [],
    'Tomorrow': [],
    'This Week': [],
    'Next Week': [],
    'Later': [],
    'No Date': [],
  };

  for (const item of items) {
    const d = parseDate(item.date);
    if (!d) {
      groups['No Date'].push(item);
      continue;
    }
    const day = startOfDay(d);
    if (day < today) {
      groups['Overdue'].push(item);
    } else if (day.getTime() === today.getTime()) {
      groups['Today'].push(item);
    } else if (day.getTime() === tomorrow.getTime()) {
      groups['Tomorrow'].push(item);
    } else if (day < endOfWeek) {
      groups['This Week'].push(item);
    } else if (day < endOfNextWeek) {
      groups['Next Week'].push(item);
    } else {
      groups['Later'].push(item);
    }
  }

  // Sort within each group by tier then deadline
  const order = ['Overdue', 'Today', 'Tomorrow', 'This Week', 'Next Week', 'Later', 'No Date'];
  return order
    .filter(label => groups[label].length > 0)
    .map(label => ({ label, items: sortItems(groups[label]) }));
}

// ── Hook ────────────────────────────────────────────────────────────────────

interface QuoteListResponse {
  success: boolean;
  data: Quote[];
  hasMore: boolean;
  nextCursor?: string;
}

interface JobListResponse {
  success: boolean;
  data: Job[];
  hasMore: boolean;
  nextCursor?: string;
}

export function useHomeDashboard(orgId: string | null) {
  const now = useMemo(() => new Date(), []);

  const { data: allItems = [], isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: ['homeDashboard', orgId],
    queryFn: async () => {
      // Fetch leads, quotes, and jobs in parallel
      const [leadsRes, quotesRes, jobsRes] = await Promise.all([
        api.get<QuoteListResponse>(`/quotes/${orgId}?limit=100&lifecycleStage=lead`),
        api.get<QuoteListResponse>(`/quotes/${orgId}?limit=100&lifecycleStage=quote`),
        api.get<JobListResponse>(`/jobs/${orgId}?limit=100`),
      ]);

      const items: DashboardItem[] = [];

      for (const lead of leadsRes.data) {
        const item = classifyLead(lead, now);
        if (item) items.push(item);
      }
      for (const quote of quotesRes.data) {
        const item = classifyQuote(quote, now);
        if (item) items.push(item);
      }
      for (const job of jobsRes.data) {
        const item = classifyJob(job, now);
        if (item) items.push(item);
      }

      return sortItems(items);
    },
    enabled: !!orgId,
  });

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load dashboard data') : null;

  // Memoize grouped views
  const priorityItems = useMemo(() => {
    const tiers: Record<PriorityTier, DashboardItem[]> = { 1: [], 2: [], 3: [], 4: [] };
    for (const item of allItems) {
      tiers[item.tier].push(item);
    }
    return tiers;
  }, [allItems]);

  const timelineGroups = useMemo(() => groupByTimeline(allItems, now), [allItems, now]);

  return {
    loading,
    error,
    allItems,
    priorityItems,
    timelineGroups,
    refetch,
  };
}
